import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  Building,
  Calendar,
  Flag,
  BarChart3,
  Settings,
  LogOut,
  ClipboardCheck,
  ShieldCheck,
  AlertTriangle,
  MessageSquare,
  Inbox,
  RefreshCw,
  Briefcase,
  Wallet,
  Ticket,
  Receipt,
  UserX,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { logout } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState(['main', 'management']);

  const toggleGroup = (group) => {
    setExpandedGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const navGroups = [
    {
      id: 'main',
      label: 'MAIN',
      items: [
        { to: '/', icon: Home, label: 'Dashboard' },
        { to: '/analytics', icon: BarChart3, label: 'Analytics' },
      ]
    },
    {
      id: 'management',
      label: 'MANAGEMENT',
      items: [
        { to: '/property-requests', icon: ClipboardCheck, label: 'Property Requests' },
        { to: '/verifications', icon: ShieldCheck, label: 'Host Verifications' },
        { to: '/company-verifications', icon: Briefcase, label: 'Company Verifications' },
        { to: '/users', icon: Users, label: 'Users' },
        { to: '/properties', icon: Building, label: 'Properties' },
        { to: '/bookings', icon: Calendar, label: 'Bookings' },
      ]
    },
    {
      id: 'finance',
      label: 'FINANCE',
      items: [
        { to: '/payment-verifications', icon: Receipt, label: 'Payment Verifications' },
        { to: '/payouts', icon: Wallet, label: 'Payouts' },
        { to: '/refunds', icon: RefreshCw, label: 'Refunds' },
        { to: '/coupons', icon: Ticket, label: 'Coupons' },
      ]
    },
    {
      id: 'support',
      label: 'SUPPORT',
      items: [
        { to: '/disputes', icon: AlertTriangle, label: 'Disputes' },
        { to: '/reports', icon: Flag, label: 'Reports' },
        { to: '/messages', icon: MessageSquare, label: 'Message Alerts' },
        { to: '/support-messages', icon: Inbox, label: 'Support Messages' },
        { to: '/account-deletions', icon: UserX, label: 'Account Deletions' },
      ]
    },
    {
      id: 'system',
      label: 'SYSTEM',
      items: [
        { to: '/settings', icon: Settings, label: 'Settings' },
      ]
    }
  ];

  const sidebarWidth = isCollapsed ? 'w-[72px]' : 'w-[260px]';

  return (
    <>
      {/* Mobile Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-30 transition-opacity duration-300 lg:hidden ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }}
      />

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex-shrink-0 flex flex-col overflow-hidden transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } ${sidebarWidth}`}
        style={{
          backgroundColor: colors.sidebar,
          borderRight: `1px solid ${colors.sidebarBorder}`,
        }}
      >
        {/* Logo Area */}
        <div
          className="flex items-center px-5 h-[68px] flex-shrink-0"
          style={{ borderBottom: `1px solid ${colors.sidebarBorder}` }}
        >
          {!isCollapsed ? (
            <div className="flex items-center gap-3">
              <img src='/manam.png' alt='logo' className='w-10 h-10' />
              <div>
                <h1
                  className="text-[15px] font-bold tracking-tight"
                  style={{ color: colors.text }}
                >
                  Manam
                </h1>
                <p
                  className="text-[11px] leading-tight font-medium uppercase tracking-wider"
                  style={{ color: colors.textMuted }}
                >
                  Admin Panel
                </p>
              </div>
            </div>
          ) : (
            <img src='/manam.png' alt='logo' className='w-10 h-10' />

          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-3">
          {navGroups.map((group) => {
            const isGroupExpanded = expandedGroups.includes(group.id);

            return (
              <div key={group.id} className="mb-2">
                {/* Group Label */}
                {!isCollapsed && (
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className="flex items-center justify-between w-full px-3 py-2 mb-1"
                  >
                    <span
                      className="text-[10.5px] font-semibold uppercase tracking-[0.1em]"
                      style={{ color: colors.textMuted }}
                    >
                      {group.label}
                    </span>
                    <ChevronDown
                      className="w-3 h-3 transition-transform duration-200"
                      style={{
                        color: colors.textMuted,
                        transform: isGroupExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                    />
                  </button>
                )}

                {/* Group Items */}
                {(isGroupExpanded || isCollapsed) && (
                  <ul className="space-y-0.5">
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.to ||
                        (item.to !== '/' && location.pathname.startsWith(item.to));

                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            onClick={onClose}
                            className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-[13px] group ${isActive ? 'active' : ''
                              }`}
                            style={{
                              backgroundColor: isActive
                                ? colors.sidebarActive
                                : 'transparent',
                              color: isActive
                                ? colors.primary
                                : colors.textSecondary,
                            }}
                            title={isCollapsed ? item.label : undefined}
                          >
                            <div
                              className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 transition-colors"
                              style={{
                                backgroundColor: isActive
                                  ? colors.primaryGhost
                                  : 'transparent',
                              }}
                            >
                              <item.icon
                                className="w-[18px] h-[18px] transition-colors"
                                style={{
                                  color: isActive
                                    ? colors.primary
                                    : colors.textMuted,
                                }}
                              />
                            </div>
                            {!isCollapsed && (
                              <span className="truncate">{item.label}</span>
                            )}
                            {!isCollapsed && isActive && (
                              <ChevronRight
                                className="w-3.5 h-3.5 ml-auto flex-shrink-0"
                                style={{ color: colors.primary }}
                              />
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div
          className="flex-shrink-0 px-3 py-3"
          style={{ borderTop: `1px solid ${colors.sidebarBorder}` }}
        >
          {/* Collapse Toggle (desktop only) */}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-colors mb-2"
            style={{ color: colors.textMuted }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.sidebarHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {isCollapsed ? (
              <ChevronsRight className="w-[18px] h-[18px]" />
            ) : (
              <>
                <ChevronsLeft className="w-[18px] h-[18px] mr-3" />
                <span className="text-[13px] font-medium">Collapse</span>
              </>
            )}
          </button>

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2.5 rounded-lg transition-colors group"
            style={{ color: colors.textMuted }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.errorGhost;
              e.currentTarget.style.color = colors.error;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = colors.textMuted;
            }}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0">
              <LogOut className="w-[18px] h-[18px]" />
            </div>
            {!isCollapsed && (
              <span className="text-[13px] font-medium ml-3">Logout</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

Sidebar.defaultProps = {
  isOpen: false,
  onClose: () => { },
  isCollapsed: false,
  onToggleCollapse: () => { },
};

export default Sidebar;
